import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { ToolbarService } from '../../../core/services/toolbar.service';
import { ApiService } from '../../../core/services/api.service';
import { Status, statusValues } from '../../models/expenses';
import { debounceTime, delay, map, skip, tap } from 'rxjs/operators';
import { LiabilityStatus, LiabilityStatusFilter, LiabilityStatusFilterValue, statusMap } from '../../models/model';
import { LiabilityService } from '../../services/liability.service';
import { LoadingService } from '../../../shared/services/loading.service';
import { Column } from '../../../shared/models/table';
import { PaykaCurrencyPipe } from '../../../shared/utils/pipes/payka-currency.pipe';
import { DatePipe, DecimalPipe } from '@angular/common';
import { Liability } from '../../models/liability.model';
import { RxStompState } from '@stomp/rx-stomp';
import { SearchLiabilities } from '../../models/search-liabilities.model';
import { AppConfig } from '../../../core/services/app-config.service';
import { PaymentService } from '../../../pay/services/payment.service';
import { AddToCartEventSource } from '../../../pay/models/add-to-cart-event-source.enum';
import { HandsetService } from '../../../core/services/handset.service';
import { Subscription } from 'rxjs/internal/Subscription';
import { CartStoreService } from '../../../shared/services/cart.service';
import { FormControl } from '@angular/forms';
import { CheckedLiability } from '../../../shared/models/cart-state.model';
import { TableComponent } from '../../../shared/components/table/table.component';
import { CheckedLiabilitiesStoreService } from '../../../shared/services/checked-liabilities.service';
import { LoaderButtonType } from '../../../shared/models/loader-button-type.enum';

@Component({
standalone: false,
  selector: 'app-liability-list',
  styleUrls: ['./liability-list.component.scss'],
  templateUrl: './liability-list.component.html',
  providers: [PaykaCurrencyPipe, DecimalPipe],
})
export class LiabilityListComponent implements OnInit, OnDestroy {
  @ViewChild(TableComponent) table!: TableComponent<Liability>;
  public selected = [];
  public deselected = [];
  status: LiabilityStatus | null;
  public imageLoaded = false;
  protected readonly statusValues = statusValues;
  protected readonly Object = Object;
  protected readonly Status = Status;
  protected readonly LiabilityStatus = LiabilityStatus;
  protected readonly statusMap = statusMap;
  currencyPipeInstance: PaykaCurrencyPipe = new PaykaCurrencyPipe(new DecimalPipe('pl'));
  datePipeInstance: DatePipe = new DatePipe('pl');
  public websocketStatus$: Observable<RxStompState>;

  public data$: Observable<SearchLiabilities>;
  private actualSearchLiabilities: SearchLiabilities;


  private cartSubscription: Subscription;
  private removingIdsSubscription: Subscription;
  private checkedIdsSubscription: Subscription;
  private addingIdsSubscription: Subscription;

  private _selectedfilter$ = new BehaviorSubject<LiabilityStatusFilter>(null);
  private _selectedFilterAsObservable: Observable<LiabilityStatusFilter>;

  public searchQuerySubject$ = new BehaviorSubject<string>(null);
  public searchQueryAsObservable: Observable<string>;

  public statusFilters: LiabilityStatusFilter[] = [
    {
      name: LiabilityStatusFilterValue.PAID,
      description: 'Rozliczony',
      filterDescription: 'Rozliczone',
      statuses: [LiabilityStatus.PAID_ON_PLATFORM, LiabilityStatus.PAID_OUTSIDE_PLATFORM],
    },
    {
      name: LiabilityStatusFilterValue.OVERDUE,
      filterDescription: 'Po terminie',
      description: 'Po terminie',
      statuses: [LiabilityStatus.OVERDUE],
    },
    {
      name: LiabilityStatusFilterValue.NOT_PAID,
      filterDescription: 'Przed terminem',
      description: 'Przed terminem',
      statuses: [LiabilityStatus.NOT_PAID, LiabilityStatus.UNKNOWN, LiabilityStatus.UNKNOWN],
    },
  ];

  public columns: Column[] = [
    {
      name: 'paymentDue',
      label: 'Data',
      pipe: this.datePipeInstance,
      pipeParam: 'dd.MM.yyyy r.',
      mobileViewDetails: {
        column: 1,
        row: 3,
      },
      style: this.desktopStyle,
      width: '110px',
    },
    {
      name: 'billerName',
      label: 'Dostawca',
      mobileViewDetails: {
        column: 1,
        row: 1,
      },
      width: '200px',
      sMaxWidth: 'calc(100vw - 125px)',
      mMaxWidth: 'calc(100vw - 125px)',
      style: this.desktopStyle,
    },
    {
      name: 'amount',
      label: 'Kwota',
      pipe: this.currencyPipeInstance,
      mobileViewDetails: {
        column: 2,
        row: 1,
      },
      width: '100px',
      mobileStyle: this.amountToPayStyle,
      style: this.desktopStyle,
    },
    {
      hide: true,
      hideOnMobile: true,
      name: 'tagsWithStyle',
      label: 'Tagi',
      mobileViewDetails: {
        column: 2,
        row: 2,
      },
      isHtml: true,
    },
    {
      hideOnMobile: true,
      name: 'titleWithTags',
      label: 'Tytuł',
      isHtml: true,
      style: this.titleDesktopStyle,
      width: '335',
      maxWidth: '335',
    },
    {
      name: 'statusText',
      label: 'Status',
      iconBefore: 'ellipse',
      iconBeforeStyle: this.iconBeforeStyle,
      style: this.statusStyle,
      mobileViewDetails: {
        column: 1,
        row: 2,
      },
      width: '135px',
      mobileStyle: this.statusMobileStyle,
    }];

  public loadingPastLiabilities = false;


  public cartAmountToPay = 0;


  public disabled = false;
  public loaded = false;
  public payableIds: string[] = [];
  public unsubscribe = new Subject();
  public payableLiabilities: Liability[] = [];
  public initialEmpty = false;


  public input = new FormControl('');
  private initialStatus: LiabilityStatusFilter;
  private checkAll = false;

  constructor(
    public appConfig: AppConfig,
    private route: ActivatedRoute,
    public router: Router,
    public handsetService: HandsetService,
    private toolbar: ToolbarService,
    public loadingService: LoadingService,
    private liabilityService: LiabilityService,
    public api: ApiService,
    public paymentService: PaymentService,
    public cartService: CartStoreService,
    private checkedLiabilitiesStore: CheckedLiabilitiesStoreService,
  ) {
    this.toolbar.init(this.route);
    this._selectedFilterAsObservable = this._selectedfilter$.asObservable();
    this.searchQueryAsObservable = this.searchQuerySubject$.asObservable();

    let state = this.router.getCurrentNavigation()?.extras.state;
    let query = state?.query;
    if (query) {
      this.input.setValue(query);
    }
    let status = state?.status;
    if (status) {
      this.initialStatus = this.statusFilters.find(filter => filter.name === status);
    }

  }

  public ngOnInit(): void {
    this.cartSubscription = this.cartService.state$.subscribe(
      value => {
        this.cartAmountToPay = value.newAmount;
      },
    );

    this.removingIdsSubscription = this.cartService.removingIdsFromCart$
      .subscribe(
        ids => {
          if (this.actualSearchLiabilities && ids && ids.length > 0) {
            this.table.deselectElements(this.actualSearchLiabilities.liabilities
              .filter(value => ids.includes(value.id)));
          }
        },
      );

    this.addingIdsSubscription = this.cartService.addedIdsToCart$
      .subscribe(
        ids => {
          if (this.actualSearchLiabilities && this.actualSearchLiabilities.liabilities && ids && ids.length > 0) {
            this.table.selectElements(this.actualSearchLiabilities.liabilities
              .filter(value => ids.includes(value.id)));
          }
        },
      );

    this.input.valueChanges.subscribe(value => {
      this.searchQuerySubject$.next(value);
    });

    if (this.route.snapshot.queryParams.check) {
      this.checkAll = this.route.snapshot.queryParams.check === 'PAYABLE';
    }

    if (this.input.value) {
      this.searchQuerySubject$.next(this.input.value);
    }
    this.searchQueryAsObservable
      .pipe(
        debounceTime(400))
      .subscribe(value => {
        let lowerCaseQuery = value ? value.toLowerCase() : '';
        this.liabilityService.search$.next(lowerCaseQuery);
      });


    this.websocketStatus$ = this.api.stompClientStatus$;

    this.data$ = this.liabilityService.data$.pipe(
      debounceTime(200),
      map(data => this.processLiabilities(data, null)),
      tap((value: SearchLiabilities) => {
        this.actualSearchLiabilities = value;
      }),
      delay(400),
      tap(() => {
        if (this.initialStatus) {
          this._selectedfilter$.next(this.initialStatus);
        }


        this.loaded = true;
      }),
    );

    this._selectedFilterAsObservable.pipe(
      skip(1),
      debounceTime(20))
      .subscribe(value => {
        this.processLiabilities(this.actualSearchLiabilities, value);
      });

  }

  public ngOnDestroy(): void {
    this.unsubscribe.next(true);
    this.unsubscribe.complete();
    this.liabilityService.liabilitiesData$.next(null);
    this.cartSubscription.unsubscribe();
    this.removingIdsSubscription.unsubscribe();
    this.addingIdsSubscription.unsubscribe();
  }


  public processLiabilities(data: SearchLiabilities, filter: LiabilityStatusFilter): SearchLiabilities {
    const sessionSelectedIds = this.cartService.getIds();

    this.payableLiabilities = this.getVisiblePayableLiabilities(data);

    data.liabilities = this.mapSearchLiabilitiesToLiabilities(data, filter, sessionSelectedIds);

    data.liabilities = data.liabilities.map(item => {
      item.isDisabled = (!item.verified || item.status === LiabilityStatus.PAID_ON_PLATFORM
        || item.status === LiabilityStatus.PAID_OUTSIDE_PLATFORM || item.inCart || item.payable === false);
      return item;
    });

    if (this.checkAll) {
      this.cartService.addCheckedLiabilityList(
        data.liabilities.filter(value => value.isSelected && !value.inCart)
          .map(value => ({
              id: value.id,
              amountToPay: value.amountToPay,
            }),
          ));
    }

    this.processObjectsInCurrentMonth(data.liabilities);
    this.payableIds = this.payableLiabilities.map((l) => l.id);

    this.initialEmpty = !data?.months?.length;
    return data;
  }

  private mapSearchLiabilitiesToLiabilities(data: SearchLiabilities, status: LiabilityStatusFilter,
                                            sessionSelectedIds: string[]): Liability[] {
    const containerStyles = `
     display: flex;
      align-items: start;
      overflow: hidden;`;
    const tagStyles = `
      text-overflow: ellipsis;
      border: 1px solid #d8d8d8;
      border-radius: 10px;
      gap: 8px;
      padding: 1px 8px;
      text-wrap: nowrap;
      box-sizing: border-box;
      background-color: #F6F6F6`;
    const tagContainerStyles = `
      display: flex;
      flex: 1 1 0;
      overflow-x: auto;
      flex-direction: row;
      align-items: center;
      overflow: auto;
      text-overflow: ellipsis;
      scrollbar-width: none;
      min-width: 0;
      gap: 8px;
      white-space: pre`;

    const tagHtml = (tag: string) => `<div style="${tagStyles}">${tag}</div>`;

    var now = new Date();
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(now.getMonth() - 1);

    const oneMonthFromNow = new Date();
    oneMonthFromNow.setMonth(now.getMonth() + 3, 1);
    return data.months
      .reduce((acc, next) => [...acc, ...next.liabilities
        .map(it => {
          var date = new Date(it.paymentDue);
          return ({
            ...it,
            amount: it.amountToPay > 0 ? it.amountToPay : it.amount,
            isSelected: it.verified && (((it.inCart || (sessionSelectedIds !== null
                  && sessionSelectedIds.includes(it.id)))
                || (this.checkAll && date >= oneMonthAgo && date < oneMonthFromNow))
              && it.status !== LiabilityStatus.PAID_OUTSIDE_PLATFORM && it.status !== LiabilityStatus.PAID_ON_PLATFORM),
            statusText: statusMap[it.newStatus].description,
            titleWithTags: `<div style="${containerStyles}; max-width: 300px; gap: 16px">
              ${it.title}
              <div style="${tagContainerStyles}">
                ${it.tags.map(it => tagHtml(it.name)).join('')}
              </div>
          </div>`,
            tagsWithStyle: `<div style="${containerStyles}; float: right; max-width: 200px; padding-top: 8px">
              <div style="${tagContainerStyles}">
                ${it.tags.map(it => tagHtml(it.name)).join('')}
              </div>
          </div>`,
          });
        })
        .filter(value => status ? status.statuses.includes(value.status) : true),
      ], [] as Liability[]);
  }

  private processObjectsInCurrentMonth(objects: Liability[]) {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    const objectsInCurrentMonth = objects.filter(obj => {

      const objDate = new Date(obj.paymentDue);
      return objDate.getMonth() === currentMonth && objDate.getFullYear() === currentYear;
    });

    objectsInCurrentMonth.sort((a, b) => new Date(b.paymentDue).getTime() - new Date(a.paymentDue).getTime());

    objectsInCurrentMonth.forEach((obj, index, array) => {
      obj.firstInCurrentMonth = index === 0;
      obj.lastInCurrentMonth = index === array.length - 1;
      obj.middleInCurrentMonth = index > 0 && index < array.length - 1;
    });
    return objectsInCurrentMonth;
  }

  public addLiability(): void {
    this.router.navigate(['/liabilities/add']);
  }

  private amountToPayStyle() {
    return {
      'font-weight': 'bold',
      'text-align': 'right',
      overflow: 'hidden',
      'white-space': 'nowrap',
    };
  }

  private desktopStyle() {
    return {
      'max-width': '240px',
      'white-space': 'nowrap',
      overflow: 'visible',
    };
  }

  private titleDesktopStyle() {
    return {
      'max-width': '280px',
      'white-space': 'nowrap',
      overflow: 'hidden',
    };
  }

  private iconBeforeStyle(liability: Liability): any {
    if (!liability.verified) {
      return {
        color: '#D9D9D9',
      };
    }
    if (LiabilityStatus.PAID_ON_PLATFORM === liability.status) {
      return {
        color: '#C49BBB',
      };
    } else if (LiabilityStatus.PAID_OUTSIDE_PLATFORM === liability.status) {
      return {
        color: '#C49BBB',
      };
    } else if (LiabilityStatus.OVERDUE === liability.status) {
      return {
        color: '#C92121',
      };
    } else if (LiabilityStatus.NOT_PAID === liability.status) {
      return {
        color: '#575481',
      };
    } else if (LiabilityStatus.UNKNOWN === liability.status) {
      return {
        color: '#19297C',
      };
    } else {
      return { color: '#575481' };
    }
  }

  private statusMobileStyle(liability: Liability): any {
    if (!liability.verified) {
      return {
        color: '#D9D9D9',
        'font-weight': 'bold',
      };
    }

    if (LiabilityStatus.PAID_ON_PLATFORM === liability.status) {
      return {
        color: '#C49BBB',
        'font-weight': 'bold',
      };
    } else if (LiabilityStatus.PAID_OUTSIDE_PLATFORM === liability.status) {
      return {
        color: '#C49BBB',
        'font-weight': 'bold',
      };
    } else if (LiabilityStatus.OVERDUE === liability.status) {
      return {
        color: '#C92121',
        'font-weight': 'bold',
      };
    } else if (LiabilityStatus.NOT_PAID === liability.status) {
      return {
        color: '#575481',
        'font-weight': 'bold',
      };
    } else if (LiabilityStatus.UNKNOWN === liability.status) {
      return {
        color: '#19297C',
        'font-weight': 'bold',
      };
    } else {
      return {
        color: '#575481',
        'font-weight': 'bold',
      };
    }
  }


  private statusStyle(liability: Liability): any {

    if (!liability.verified) {
      return {
        color: '#D9D9D9',
        'font-weight': 'bold',
        overflow: 'hidden',
        'white-space': 'nowrap',
      };
    } else {

      if (LiabilityStatus.PAID_ON_PLATFORM === liability.status) {
        return {
          color: '#C49BBB',
          'font-weight': 'bold',
          overflow: 'hidden',
          'white-space': 'nowrap',
        };
      } else if (LiabilityStatus.PAID_OUTSIDE_PLATFORM === liability.status) {
        return {
          color: '#C49BBB',
          'font-weight': 'bold',
          overflow: 'hidden',
          'white-space': 'nowrap',
        };
      } else if (LiabilityStatus.OVERDUE === liability.status) {
        return {
          color: '#C92121',
          'font-weight': 'bold',
          overflow: 'hidden',
          'white-space': 'nowrap',
        };
      } else if (LiabilityStatus.NOT_PAID === liability.status) {
        return {
          color: '#575481',
          'font-weight': 'bold',
          overflow: 'hidden',
          'white-space': 'nowrap',
        };
      } else if (LiabilityStatus.UNKNOWN === liability.status) {
        return {
          color: '#19297C',
          'font-weight': 'bold',
          overflow: 'hidden',
          'white-space': 'nowrap',
        };
      } else {
        return {
          color: '#575481',
          'font-weight': 'bold',
          overflow: 'hidden',
          'white-space': 'nowrap',
        };
      }
    }
  }

  public onStatusSelected(status: LiabilityStatusFilter) {
    this._selectedfilter$.next(status);
  }

  public onAllSelected() {
    this._selectedfilter$.next(null);
  }

  get selectedFilter() {
    return this._selectedfilter$.value;
  }

  public getLiabilities() {
    return this.actualSearchLiabilities.liabilities;
  }

  public clearSearch(): void {
    this.input.setValue('');
    this.searchQuerySubject$.next('');
  }

  public rowStyle(liability: Liability): any {
    const styles: Record<string, string | number> = {};
    styles.cursor = 'pointer';
    if (!liability.verified) {
      styles.color = '#D9D9D9';
    } else {
      if (LiabilityStatus.OVERDUE === liability.status) {
        styles.color = '#C92121';
      }
    }

    if (liability.middleInCurrentMonth) {
      styles['border-right'] = '2px solid #19297C';
      styles['border-left'] = '2px solid #19297C';
    }
    if (liability.lastInCurrentMonth) {
      styles['border-bottom'] = '2px solid #19297C';
      styles['border-right'] = '2px solid #19297C';
      styles['border-left'] = '2px solid #19297C';
    }
    if (liability.firstInCurrentMonth) {
      styles['border-top'] = '2px solid #19297C';
      styles['border-right'] = '2px solid #19297C';
      styles['border-left'] = '2px solid #19297C';
    }

    return styles;
  }

  public loadMorePastWithLoader(): void {
    this.loadingPastLiabilities = true;

    this.liabilityService.loadMorePast().subscribe(() => {
      this.loadingPastLiabilities = false;
    });
  }


  public getVisiblePayableLiabilities(liabilities: SearchLiabilities): Liability[] {
    return liabilities?.months?.length > 0
      ? ([] as Liability[]).concat(
        ...liabilities?.months?.map((month) => month.liabilities
          .filter((liability) => liability.payable)),
      )
      : [];
  }

  onChangeCheckedLiabilies(items: CheckedLiability[], isAdded: boolean) {
    if (isAdded) {
      this.cartService.addCheckedLiabilityList(items);
    } else {
      this.cartService.removeList(items.map(value => value.id));
    }
  }

  onCheckList(items: Liability[], isAdded: boolean) {
    items = items.filter(value => !value.inCart && value.payable);
    if (isAdded) {
      this.cartService.addLiabilityList(items);
    } else {
      this.cartService.removeList(items.map(value => value.id));
    }
  }

  private sumAmounts(items: CheckedLiability[]) {
    if (!items) {
      return 0;
    } else {
      return items.reduce((sum, currentValue) => sum + currentValue.amountToPay, 0);

    }
  }


  showDetails(item: { id: string; }) {
    const baseUrl = '/liabilities/all';
    this.router.navigate(['/liabilities/', item.id], {
        queryParams:
          {
            returnUrl: baseUrl,
            query: this.searchQuerySubject$.getValue(),
            status: this._selectedfilter$?.value?.name,
          },
      },
    );
  }

  addLiabilitiesToCart() {
    this.paymentService
      .initPayment(this.cartService.getIds(), AddToCartEventSource.LIABILITIES, '/liabilities')
      .subscribe(() => {
      });
  }

  onImageLoad() {
    this.imageLoaded = true;
  }

  private toCheckedLiability(list: Liability[]): CheckedLiability[] {
    if (!list) {
      return [];
    } else {
      return list.map(value => ({
        id: value.id,
        amountToPay: value.amountToPay,
      }));
    }
  }

  protected readonly LoaderButtonType = LoaderButtonType;
}


